// super-admin/edit-pending-org-dialog.component.ts
import { Component, Inject, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Organization } from 'fhir/r4';

import { DynamicFormsV2Component } from '../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { CodeableConceptField, CodeField, GroupField, IndividualField, IndividualReferenceField, ReferenceFieldArray, formMetaData } from '../shared/dynamic-forms.interface2';
import { FormFieldsSelectDataService } from '../shared/form-fields-select-data.service';
import { ErrorService } from '../shared/error.service';
import { SuccessMessageComponent } from '../shared/success-message/success-message.component';
import { SuperAdminService } from '../services/super-admin.service';

type FormFields = IndividualField | ReferenceFieldArray | CodeableConceptField | CodeField | IndividualReferenceField | GroupField;

export interface EditPendingOrgDialogData {
  approvalId: string;          // OrganizationApproval.Id (Guid)
  organizationName: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  adminPhone: string;
  fhirJson?: string;            // raw FHIR snapshot, if the caller has it (e.g. from GetAllOrganizations)
}

@Component({
  selector: 'app-edit-pending-org-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DynamicFormsV2Component,
    MatDialogModule,
    MatSnackBarModule,
    MatIconModule,
    MatButtonModule
  ],
  template: `
    <h2 mat-dialog-title>Edit Registration — {{ data.organizationName }}</h2>

    <div mat-dialog-content>
      @if (fieldsReady) {
        <app-dynamic-forms-v2
  [formMetaData]="formMetaData"
  [formFields]="formFields"
  [prepopulatedData]="initialFormValue"
  [resetOnSubmit]="false"
  (formSubmitted)="onFormSubmit($event)">
</app-dynamic-forms-v2>
      } @else {
        <div class="g-just-flex flex-column gap-10" style="align-items:center; padding: 24px;">
          <span>Loading form…</span>
        </div>
      }
    </div>

    <div mat-dialog-actions align="end">
      <button mat-button (click)="dialogRef.close()">Cancel</button>
    </div>
  `
})
export class EditPendingOrgDialogComponent implements OnInit {
  private http = inject(HttpClient);
  private formFieldsDataService = inject(FormFieldsSelectDataService);
  private snackBar = inject(MatSnackBar);
  private errorService = inject(ErrorService);
  private service = inject(SuperAdminService);

  formMetaData!: formMetaData;
  formFields!: FormFields[];
  initialFormValue: any;
  fieldsReady = false;

  constructor(
    public dialogRef: MatDialogRef<EditPendingOrgDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditPendingOrgDialogData
  ) {}

  ngOnInit() {
    this.formMetaData = {
      formName: 'Edit Registration',
      formDescription: `Update the pending registration for ${this.data.organizationName}`,
      submitText: 'Save Changes'
    };

    forkJoin({
      contactPointUse: this.formFieldsDataService.getFormFieldSelectData('patient', 'contactPointUse'),
      contactPointSystem: this.formFieldsDataService.getFormFieldSelectData('patient', 'contactPointSystem')
    }).subscribe({
      next: (g: any) => {
        this.formFields = this.getFormFields({
          contactPointUse: this.transform(g.contactPointUse),
          contactPointSystem: this.transform(g.contactPointSystem)
        });
        this.initialFormValue = this.buildInitialFormValue();
        this.fieldsReady = true;
      },
      error: () => {
        this.formFields = this.getFormFields({ contactPointUse: [], contactPointSystem: [] });
        this.initialFormValue = this.buildInitialFormValue();
        this.fieldsReady = true;
      }
    });
  }

  private transform(data: any[]) {
    if (!data || !Array.isArray(data)) return [];
    return data.map(item => ({
      system: 'http://terminology.hl7.org/CodeSystem/contact-point-system',
      code: item.code || item,
      display: item.display || item
    }));
  }

  /** Pre-fill the form from the existing pending FHIR snapshot + admin contact fields. */
  private buildInitialFormValue(): any {
    let org: Partial<Organization> = {};
    if (this.data.fhirJson) {
      try { org = JSON.parse(this.data.fhirJson); } catch { /* fall back to blank org fields */ }
    }

    const phone = org.telecom?.find(t => t.system === 'phone');
    const email = org.telecom?.find(t => t.system === 'email');
    const other = (org.telecom || []).filter(t => t !== phone && t !== email);
    const addr = org.address?.[0];
    const descriptionExt = org.extension?.find(
      e => e.url === 'http://elikita.org/fhir/StructureDefinition/organization-description'
    );

    return {
      type: org.type?.[0]?.text ?? '',
      name: org.name ?? this.data.organizationName,
      description: descriptionExt?.valueString ?? '',
      phone_telecom: phone ? { value: phone.value, system: 'phone', use: phone.use ?? 'work' } : undefined,
      email_telecom: email ? { value: email.value, system: 'email', use: email.use ?? 'work' } : undefined,
      telecom: other.map(t => ({ value: t.value, system: t.system, use: t.use })),
      address: addr ? [{
        line: addr.line?.[0] ?? '',
        city: addr.city ?? '',
        state: addr.state ?? '',
        country: addr.country ?? '',
        postalCode: addr.postalCode ?? ''
      }] : [],
      admin_details: {
        firstName: this.data.adminFirstName,
        lastName: this.data.adminLastName,
        email: this.data.adminEmail,
        phone: this.data.adminPhone
      }
    };
  }

  private getFormFields(g: { contactPointUse: any[]; contactPointSystem: any[] }): FormFields[] {
    // Same field schema as hospital-registration.component.ts's getHospitalFormFields,
    // minus nothing — a superadmin editing a pending org can touch everything
    // the original registrant could. Kept identical so validation behaves the same way.
    return [
      <IndividualField>{
        generalProperties: {
          fieldApiName: 'type', fieldName: "Organization's Category", fieldType: 'IndividualField',
          fieldLabel: 'Organization Category', isArray: false, isGroup: false,
          auth: { read: 'all', write: 'doctor, nurse' }
        }
      },
      <IndividualField>{
        generalProperties: {
          fieldApiName: 'name', fieldName: 'Name of Organization', fieldType: 'IndividualField',
          fieldLabel: 'Name of Organization', isArray: false, isGroup: false,
          validations: [{ type: 'default', name: 'required', isFunction: false }],
          auth: { read: 'all', write: 'doctor, nurse' }
        }
      },
      <IndividualField>{
        generalProperties: {
          fieldApiName: 'description', fieldName: 'Description', fieldType: 'IndividualField',
          fieldLabel: 'Description', inputType: 'textarea', isArray: false, isGroup: false,
          auth: { read: 'all', write: 'doctor, nurse' }
        }
      },
      <GroupField>{
        generalProperties: {
          fieldApiName: 'phone_telecom', fieldName: 'Phone Number', fieldLabel: 'Phone Number',
          fieldType: 'IndividualField', isArray: false, isGroup: true,
          auth: { read: 'all', write: 'doctor, nurse' }
        },
        keys: ['value', 'system', 'use'],
        groupFields: {
          value: <IndividualField>{ generalProperties: {
            fieldApiName: 'value', fieldName: 'Phone Number', fieldLabel: 'Phone Number',
            validations: [{ type: 'default', name: 'required', isFunction: false }],
            auth: { read: 'all', write: 'doctor, nurse' }, isArray: false, isGroup: false } },
          system: <CodeField>{ generalProperties: {
            fieldApiName: 'system', fieldName: 'Contact Platform', fieldLabel: 'Contact Platform',
            fieldType: 'CodeField', value: 'phone', data: g.contactPointSystem, isHidden: true,
            auth: { read: 'all', write: 'doctor, nurse' }, isArray: false, isGroup: false }, data: g.contactPointSystem },
          use: <CodeField>{ generalProperties: {
            fieldApiName: 'use', fieldName: 'Contact Type', fieldLabel: 'Contact Type',
            fieldType: 'CodeField', value: 'work', data: g.contactPointUse, isHidden: true,
            auth: { read: 'all', write: 'doctor, nurse' }, isArray: false, isGroup: false }, data: g.contactPointUse }
        }
      },
      <GroupField>{
        generalProperties: {
          fieldApiName: 'email_telecom', fieldName: 'Email', fieldLabel: 'Email',
          fieldType: 'IndividualField', isArray: false, isGroup: true,
          auth: { read: 'all', write: 'doctor, nurse' }
        },
        keys: ['value', 'system', 'use'],
        groupFields: {
          value: <IndividualField>{ generalProperties: {
            fieldApiName: 'value', fieldName: 'Email', fieldLabel: 'Email',
            validations: [{ type: 'default', name: 'required', isFunction: false }, { type: 'default', name: 'email', isFunction: false }],
            auth: { read: 'all', write: 'doctor, nurse' }, isArray: false, isGroup: false } },
          system: <CodeField>{ generalProperties: {
            fieldApiName: 'system', fieldName: 'Contact Platform', fieldLabel: 'Contact Platform',
            fieldType: 'CodeField', value: 'email', data: g.contactPointSystem, isHidden: true,
            auth: { read: 'all', write: 'doctor, nurse' }, isArray: false, isGroup: false }, data: g.contactPointSystem },
          use: <CodeField>{ generalProperties: {
            fieldApiName: 'use', fieldName: 'Contact Type', fieldLabel: 'Contact Type',
            fieldType: 'CodeField', value: 'work', data: g.contactPointUse, isHidden: true,
            auth: { read: 'all', write: 'doctor, nurse' }, isArray: false, isGroup: false }, data: g.contactPointUse }
        }
      },
      <GroupField>{
        generalProperties: {
          fieldApiName: 'address', fieldName: 'Address', fieldLabel: 'Address',
          fieldType: 'IndividualField', isArray: true, isGroup: true,
          auth: { read: 'all', write: 'doctor, nurse' }
        },
        keys: ['line', 'city', 'state', 'country', 'postalCode'],
        groupFields: {
          line: <IndividualField>{ generalProperties: {
            fieldApiName: 'line', fieldName: 'Address Line', fieldLabel: 'Address Line',
            fieldType: 'IndividualField', validations: [{ type: 'default', name: 'required', isFunction: false }],
            auth: { read: 'all', write: 'doctor, nurse' }, isArray: false, isGroup: false } },
          city: <IndividualField>{ generalProperties: {
            fieldApiName: 'city', fieldName: 'City', fieldLabel: 'City', fieldType: 'IndividualField',
            validations: [{ type: 'default', name: 'required', isFunction: false }],
            auth: { read: 'all', write: 'doctor, nurse' }, isArray: false, isGroup: false } },
          state: <IndividualField>{ generalProperties: {
            fieldApiName: 'state', fieldName: 'State', fieldLabel: 'State', fieldType: 'IndividualField',
            validations: [{ type: 'default', name: 'required', isFunction: false }],
            auth: { read: 'all', write: 'doctor, nurse' }, isArray: false, isGroup: false } },
          country: <IndividualField>{ generalProperties: {
            fieldApiName: 'country', fieldName: 'Country', fieldLabel: 'Country', fieldType: 'IndividualField',
            validations: [{ type: 'default', name: 'required', isFunction: false }],
            auth: { read: 'all', write: 'doctor, nurse' }, isArray: false, isGroup: false } },
          postalCode: <IndividualField>{ generalProperties: {
            fieldApiName: 'postalCode', fieldName: 'Postal Code', fieldLabel: 'Postal Code',
            fieldType: 'IndividualField', auth: { read: 'all', write: 'doctor, nurse' }, isArray: false, isGroup: false } }
        }
      },
      <GroupField>{
        generalProperties: {
          fieldApiName: 'admin_details', fieldName: 'Administrator Details', fieldLabel: 'Administrator Information',
          fieldType: 'IndividualField', isArray: false, isGroup: true,
          auth: { read: 'all', write: 'doctor, nurse' }
        },
        keys: ['firstName', 'lastName', 'email', 'phone'],
        groupFields: {
          firstName: <IndividualField>{ generalProperties: {
            fieldApiName: 'firstName', fieldName: 'First Name', fieldLabel: 'Admin First Name',
            fieldType: 'IndividualField', validations: [{ type: 'default', name: 'required', isFunction: false }],
            auth: { read: 'all', write: 'doctor, nurse' }, isArray: false, isGroup: false } },
          lastName: <IndividualField>{ generalProperties: {
            fieldApiName: 'lastName', fieldName: 'Last Name', fieldLabel: 'Admin Last Name',
            fieldType: 'IndividualField', validations: [{ type: 'default', name: 'required', isFunction: false }],
            auth: { read: 'all', write: 'doctor, nurse' }, isArray: false, isGroup: false } },
          email: <IndividualField>{ generalProperties: {
            fieldApiName: 'email', fieldName: 'Admin Email', fieldLabel: 'Admin Email',
            fieldType: 'IndividualField', validations: [{ type: 'default', name: 'required', isFunction: false }, { type: 'default', name: 'email', isFunction: false }],
            auth: { read: 'all', write: 'doctor, nurse' }, isArray: false, isGroup: false } },
          phone: <IndividualField>{ generalProperties: {
            fieldApiName: 'phone', fieldName: 'Admin Phone', fieldLabel: 'Admin Phone',
            fieldType: 'IndividualField', validations: [{ type: 'default', name: 'required', isFunction: false }],
            auth: { read: 'all', write: 'doctor, nurse' }, isArray: false, isGroup: false } }
        }
      }
    ];
  }

  onFormSubmit(formData: any) {
    if (!formData.admin_details?.firstName || !formData.admin_details?.lastName ||
        !formData.admin_details?.email || !formData.admin_details?.phone) {
      this.errorService.openandCloseError('Administrator First Name, Last Name, Email, and Phone are required.');
      return;
    }

    const fhirOrg = this.buildFhirOrganization(formData);
    if (!fhirOrg.name) {
      this.errorService.openandCloseError('Organization name is required.');
      return;
    }

    const pruned = this.pruneEmpty(structuredClone ? structuredClone(fhirOrg) : JSON.parse(JSON.stringify(fhirOrg)));

    this.service.updatePendingOrganization(this.data.approvalId, {
      adminFirstName: formData.admin_details.firstName,
      adminLastName: formData.admin_details.lastName,
      adminEmail: formData.admin_details.email,
      adminPhone: formData.admin_details.phone,
      fhirJson: JSON.stringify(pruned)
    }).subscribe({
      next: () => {
        this.snackBar.openFromComponent(SuccessMessageComponent, {
          data: { message: `${formData.name || this.data.organizationName} updated successfully!` },
          duration: 3500,
          verticalPosition: 'top'
        });
        this.dialogRef.close({ success: true });
      },
      error: (err: any) => {
        const message = err?.error?.message || err?.error || err?.message || 'Update failed. Please try again.';
        this.snackBar.open(`Update failed: ${message}`, 'Close', { duration: 5000, panelClass: ['snackbar-error'] });
      }
    });
  }

  private buildFhirOrganization(formData: any): Organization {
    const telecom: any[] = [];
    const push = (t: any) => { if (t?.value) telecom.push({ system: t.system, value: t.value, use: t.use }); };
    push(formData.phone_telecom);
    push(formData.email_telecom);
    (formData.telecom || []).forEach(push);

    const org: Organization = {
      resourceType: 'Organization',
      id: undefined, // the backend keeps the existing FhirResourceId — never send one from the form
      name: formData.name ? String(formData.name).trim() : undefined,
      type: formData.type ? [{ text: String(formData.type).trim() }] : undefined,
      telecom: telecom.length ? telecom : undefined,
      address: Array.isArray(formData.address) && formData.address.length
        ? formData.address.map((a: any) => ({
            line: a.line ? [String(a.line).trim()] : undefined,
            city: a.city ? String(a.city).trim() : undefined,
            state: a.state ? String(a.state).trim() : undefined,
            country: a.country ? String(a.country).trim() : undefined,
            postalCode: a.postalCode ? String(a.postalCode).trim() : undefined
          }))
        : undefined,
      extension: []
    } as Organization;

    if (formData.description) {
      org.extension!.push({
        url: 'http://elikita.org/fhir/StructureDefinition/organization-description',
        valueString: String(formData.description).trim()
      });
    }
    if (!org.extension!.length) delete (org as any).extension;

    return org;
  }

  private pruneEmpty<T>(input: T): T {
    const isObj = (v: any) => v && typeof v === 'object' && !Array.isArray(v);
    const prune = (v: any): any => {
      if (Array.isArray(v)) {
        const a = v.map(prune).filter(x => x !== undefined && !(Array.isArray(x) && !x.length) && !(isObj(x) && !Object.keys(x).length));
        return a.length ? a : undefined;
      } else if (isObj(v)) {
        const e = Object.entries(v).map(([k, val]) => [k, prune(val)] as [string, any])
          .filter(([, val]) => val !== undefined && !(Array.isArray(val) && !val.length) && !(isObj(val) && !Object.keys(val).length));
        return e.length ? Object.fromEntries(e) : undefined;
      }
      return v === undefined || v === null || v === '' ? undefined : v;
    };
    return prune(input);
  }
}